import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logo_logo.svg';

const formatNumber = (value, digits = 4) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : '-';
};

const formatPnl = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}`;
};

const formatPercent = (value, digits = 2) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : '-';
};

const BackgroundGlow = () => (
  <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-0">
    <div
      className="absolute -top-[10%] -left-[10%] w-[70%] h-[70%]"
      style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.15) 0%, rgba(11,17,32,0) 70%)' }}
    />
    <div
      className="absolute -bottom-[10%] -right-[10%] w-[70%] h-[70%]"
      style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, rgba(11,17,32,0) 70%)' }}
    />
  </div>
);

const CardHeader = ({ data }) => {
  const { t } = useTranslation();

  return (
  <div className="relative z-10 px-10 pt-7 pb-6 border-b border-slate-800/60">
    <div className="grid grid-cols-[auto_1fr] items-center gap-4">
      <img src={logo} alt="Hedger Logo" className="w-[72px] h-[72px] object-contain shrink-0" />
      <div className="min-w-0">
        <div className="flex items-end justify-between gap-6">
          <h2 className="text-[38px] font-black text-white leading-[1]">Hedger</h2>
          <h3 className="text-[36px] font-black text-white leading-[1] whitespace-nowrap">
            {data.pairA} / {data.pairB}
          </h3>
        </div>
        <div className="flex items-center justify-between mt-2.5">
          <div className="text-[13px] text-blue-400 font-bold uppercase tracking-[0.22em] leading-tight">
            {t('share_card.automated_trading')}
          </div>
          <p className="text-base text-slate-400 font-mono font-medium leading-none">{data.date}</p>
        </div>
        {data.strategyName && null}
      </div>
    </div>
  </div>
  );
};

const TradeEntryRow = ({ entry, pairA, pairB }) => {
  const { t } = useTranslation();
  const amountA = Number(entry.price_a) * Number(entry.quantity_a);
  const amountB = Number(entry.price_b) * Number(entry.quantity_b);
  const amountADisplay = Number.isFinite(amountA) ? `$${formatNumber(amountA, 2)}` : '-';
  const amountBDisplay = Number.isFinite(amountB) ? `$${formatNumber(amountB, 2)}` : '-';

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl px-6 py-6 shadow-sm backdrop-blur-sm">
      <div className="grid grid-cols-[1.1fr_1fr_1fr] items-stretch gap-6">
        <div className="flex flex-col justify-center border-r border-slate-700/50 pr-6">
          <span className="text-[15px] text-slate-500 font-bold uppercase tracking-wider mb-2">
            {t('share_card.entry', { id: entry.id })}
          </span>
          <div className="text-2xl font-mono text-white font-bold leading-none mb-2">{entry.time}</div>
          <div className="text-base text-blue-400 font-mono font-medium">Z: {formatNumber(entry.zScore, 4)}</div>
        </div>

        <div className="flex flex-col justify-center pl-1">
          <span className={`text-m font-black uppercase mb-2 tracking-wider ${entry.pos_a === 'LONG' ? 'text-emerald-500' : 'text-rose-500'}`}>
            {pairA} {entry.pos_a || ''}
          </span>
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 text-[15px] mb-1.5">
            <span className="text-slate-400">{t('share_card.price')}:</span>
            <span className="font-mono text-white font-semibold text-right tabular-nums">${formatNumber(entry.price_a, 4)}</span>
          </div>
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 text-[15px] mb-1.5">
            <span className="text-slate-400">{t('share_card.qty')}:</span>
            <span className="font-mono text-slate-300 text-right tabular-nums">{formatNumber(entry.quantity_a, 4)}</span>
          </div>
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 text-[15px]">
            <span className="text-slate-400">{t('share_card.entry_amount')}:</span>
            <span className="font-mono text-white font-semibold text-right tabular-nums">{amountADisplay}</span>
          </div>
        </div>

        <div className="flex flex-col justify-center pl-6 border-l border-slate-700/50">
          <span className={`text-m font-black uppercase mb-2 tracking-wider ${entry.pos_b === 'LONG' ? 'text-emerald-500' : 'text-rose-500'}`}>
            {pairB} {entry.pos_b || ''}
          </span>
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 text-[15px] mb-1.5">
            <span className="text-slate-400">{t('share_card.price')}:</span>
            <span className="font-mono text-white font-semibold text-right tabular-nums">${formatNumber(entry.price_b, 5)}</span>
          </div>
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 text-[15px] mb-1.5">
            <span className="text-slate-400">{t('share_card.qty')}:</span>
            <span className="font-mono text-slate-300 text-right tabular-nums">{formatNumber(entry.quantity_b, 4)}</span>
          </div>
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 text-[15px]">
            <span className="text-slate-400">{t('share_card.entry_amount')}:</span>
            <span className="font-mono text-white font-semibold text-right tabular-nums">{amountBDisplay}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TradeList = ({ data }) => {
  const { t } = useTranslation();

  return (
    <div className="relative z-10 px-10 py-6 space-y-4">
      <div className="text-sm text-slate-500 font-bold uppercase tracking-widest pl-1">
        {t('share_card.trade_entries')}
      </div>
      {data.entries.map((entry) => (
        <TradeEntryRow key={entry.id} entry={entry} pairA={data.pairA} pairB={data.pairB} />
      ))}
    </div>
  );
};

const CardFooter = ({ data }) => {
  const { t } = useTranslation();
  const pnlA = Number(data.exit.pnlA || 0);
  const pnlB = Number(data.exit.pnlB || 0);
  const total = Number(data.exit.totalPnl || 0);
  const rawCommission = data.commission;
  const rawCommissionRate = data.commissionRate;
  const commissionValue = Number(rawCommission);
  const commissionRate = Number(rawCommissionRate);
  const hasCommissionValue = rawCommission !== null && rawCommission !== undefined && rawCommission !== '' && Number.isFinite(commissionValue);
  const hasCommissionRate = rawCommissionRate !== null && rawCommissionRate !== undefined && rawCommissionRate !== '' && Number.isFinite(commissionRate);
  const commissionDisplay = hasCommissionValue
    ? `$${formatNumber(commissionValue, 2)}`
    : hasCommissionRate
      ? `${formatPercent(commissionRate, 2)}%`
      : '—';

  return (
    <div className="relative z-10 px-10 pb-10">
      <div className="bg-gradient-to-r from-slate-800/90 to-slate-900/90 backdrop-blur-md border border-slate-700 rounded-3xl p-7 shadow-2xl">
        <div className="grid grid-cols-3 gap-8 mb-5 border-b border-slate-700/50 pb-5">
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase mb-2 tracking-wider">
              {t('share_card.exit', { symbol: data.pairA })}
            </div>
            <div className="text-2xl font-mono text-white font-bold leading-none mb-1">${formatNumber(data.exit.priceA, 4)}</div>
            <div className={`text-base font-bold ${pnlA >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatPnl(data.exit.pnlA)} $
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-500 font-bold uppercase mb-2 tracking-wider">
              {t('share_card.commission')}
            </div>
            <div className="text-2xl font-mono text-white font-bold leading-none mb-1">{commissionDisplay}</div>
            <div className="text-base font-semibold text-slate-400">
              {hasCommissionValue && hasCommissionRate ? `${t('share_card.rate')} ${formatPercent(commissionRate, 2)}%` : '\u2014'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 font-bold uppercase mb-2 tracking-wider">
              {t('share_card.exit', { symbol: data.pairB })}
            </div>
            <div className="text-2xl font-mono text-white font-bold leading-none mb-1">${formatNumber(data.exit.priceB, 5)}</div>
            <div className={`text-base font-bold ${pnlB >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatPnl(data.exit.pnlB)} $
            </div>
          </div>
        </div>

        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider leading-tight opacity-70 mb-3">
              {t('share_card.trades_executed_on')} <span className="text-white">Bybit</span><br />
              {t('share_card.official_partner')}
            </div>
            <div className="flex items-center">
              <div className="bg-white p-2 rounded-xl w-fit shadow-lg shadow-white/5">
                <QRCodeSVG
                  value="https://hedger.bar"
                  size={80}
                  fgColor="#0F172A"
                  bgColor="#FFFFFF"
                  level="M"
                />
              </div>
            </div>
          </div>

          <div className="text-right flex flex-col justify-start min-w-[280px] pt-1">
            <span className="block text-[34px] text-slate-400 font-bold uppercase tracking-widest mb-2 leading-none">
              {t('share_card.total_pnl')}
            </span>
            <span
              className={`-mt-1 block text-7xl font-black tracking-tighter leading-none drop-shadow-[0_0_25px_rgba(52,211,153,0.25)] ${total >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
              style={{ lineHeight: '0.9', display: 'block' }}
            >
              {formatPnl(data.exit.totalPnl)} $
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TradeShareCard = ({ data, className }) => {
  if (!data) return null;

  return (
    <div className={`w-[760px] bg-[#0B1120] relative flex flex-col overflow-hidden text-white ${className || ''}`}>
      <BackgroundGlow />
      <CardHeader data={data} />
      <TradeList data={data} />
      <CardFooter data={data} />
    </div>
  );
};

export default TradeShareCard;
